/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useContext, useMemo, useState } from "react";
import { EditorMainContext } from "../../contexts/EditorMainContext";
import { Button } from "./Button";
import { DROPDOWN_STYLE, IDropdownMenuItem } from "../DropdownMenu";
import { FC_ANCHOR, FC_DIRECTION, FC_SIZE } from "../FloatingContainer";
import { Bem, useTranslate } from "@adapptio/lib-components";

/**
 * Command button menu item
 */
export interface ICommandButtonMenuItem {
	id: string;
	label: string;
	icon?: string;
	title?: string;
	disabled?: boolean;
	selected?: boolean;
	commandId: string;
	commandArgs?: unknown[];
}

/**
 * Command Button props
 */
export interface ICommandButtonProps {
	label?: string | boolean;
	icon?: string;
	tooltip?: string;
	isLoading?: boolean;
	showLoading?: boolean;
	disabled?: boolean;
	active?: boolean;
	menuItems?: ICommandButtonMenuItem[];
	commandId?: string;
	commandArgs?: unknown[];
	menuAnchor?: FC_ANCHOR;
	menuDirection?: FC_DIRECTION;
	menuSize?: FC_SIZE;
	menuStyle?: DROPDOWN_STYLE;
	className?: string;
	mark?: string;
	onMenuOpenedChange?: (isMenuOpened: boolean) => void;
}

export interface ICommandButtonComponentProps extends ICommandButtonProps {
	baseClass: Bem;
}

/**
 * Generic Command button
 * @param props Component props
 */
export const CommandButton: FC<ICommandButtonComponentProps> = ({ children, ...props }) => {
	const ctx = useContext(EditorMainContext);
	const t = useTranslate();

	const [ primaryCommandLoading, setPrimaryCommandLoading ] = useState(false);

	const handlePrimaryClick = useCallback(() => {
		setPrimaryCommandLoading(true);

		ctx.commandsService
			.execute(props.commandId, ...(props.commandArgs || []))
			.catch((err) => {
				console.error("Failed to execute button command:", props.commandId, props.commandArgs, err);
			})
			.finally(() => {
				setPrimaryCommandLoading(false);
			});
	}, [ props.commandId, props.commandArgs ]);

	const handleMenuItemClick = useCallback(
		(item: IDropdownMenuItem) => {
			const menuItem = props.menuItems.filter((_item) => _item.id === item.id)[0];

			if (!menuItem) {
				return;
			}

			ctx.commandsService.execute(menuItem.commandId, ...(menuItem.commandArgs || [])).catch((err) => {
				console.error(
					"Failed to execute action button menu item command:",
					item.id,
					menuItem.commandId,
					menuItem.commandArgs,
					err
				);
			});
		},
		[ props.menuItems ]
	);

	const tooltip = useMemo(() => {
		if (!props.commandId) {
			return undefined;
		}

		const command = ctx.commandsService.getById(props.commandId);
		const keyCombination = ctx.keyBindingService.getKeysForCommandAsString(
			props.commandId,
			props.commandArgs
		);
		const baseTitle = props.tooltip ? props.tooltip : t("editor", command?.label);

		return baseTitle || keyCombination
			? `${baseTitle}${keyCombination ? ` (${keyCombination})` : ""}`
			: "";
	}, [ props.tooltip, props.commandId, props.commandArgs ]);

	const menuItems = useMemo(() => {
		if (!props.menuItems) {
			return [];
		}

		return props.menuItems.map((item) => {
			const keyCombination = item.commandId
				? ctx.keyBindingService.getKeysForCommandAsString(item.commandId, item.commandArgs)
				: "";

			return {
				id: item.id,
				label: item.label,
				disabled: item.disabled,
				icon: item.icon,
				selected: item.selected,
				title: item.title || "" + (keyCombination !== "" ? ` (${keyCombination})` : "")
			} as IDropdownMenuItem;
		});
	}, [ props.menuItems ]);

	let label: string = undefined;

	if (props.label === true) {
		const command = props.commandId ? ctx.commandsService.getById(props.commandId) : null;
		label = t("editor", command?.label);
	} else if (props.label) {
		label = props.label;
	}

	return (
		<Button
			label={label}
			icon={props.icon}
			tooltip={tooltip}
			isLoading={primaryCommandLoading || props.isLoading}
			showLoading={props.showLoading}
			disabled={primaryCommandLoading || props.disabled}
			active={props.active}
			menuItems={menuItems}
			menuAnchor={props.menuAnchor}
			menuDirection={props.menuDirection}
			menuSize={props.menuSize}
			menuStyle={props.menuStyle}
			onClick={props.commandId ? handlePrimaryClick : undefined}
			onMenuItemClick={menuItems.length > 0 ? handleMenuItemClick : undefined}
			onMenuOpenedChange={props.onMenuOpenedChange}
			baseClass={props.baseClass}
			className={props.className}
			mark={props.mark ?? (props.commandId ? "cmd-" + props.commandId.replace(/\./g, "_") : undefined)}
		>
			{children}
		</Button>
	);
};
